import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import { useOrganization } from '../../context/OrganizationContext';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || "https://us-central1-sistema-bks.cloudfunctions.net";

export default function WhatsAppApiConnect({ uid, onClose, onSuccess }) {
  const { context } = useOrganization();
  const { t } = useTranslation('channels');
  const { t: tv } = useTranslation('validation');
  const { t: tc } = useTranslation('common');
  const [accessToken, setAccessToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validación client-side
    if (!accessToken.trim()) {
      setError(tv('accessTokenRequired'));
      return;
    }

    if (!phoneNumberId.trim() || !/^\d+$/.test(phoneNumberId.trim())) {
      setError(tv('phoneNumeric'));
      return;
    }

    if (!wabaId.trim() || !/^\d+$/.test(wabaId.trim())) {
      setError(tv('phoneNumeric'));
      return;
    }

    setLoading(true);

    try {
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(`${API_URL}/integrations_whatsappapi_connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          accessToken: accessToken.trim(),
          phoneNumberId: phoneNumberId.trim(),
          wabaId: wabaId.trim(),
          organizationId: context.mode === 'organization' ? context.orgId : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // `message` es la frase para la persona (el tope del plan y la marca `chatPorCuenta` la
        // ponen ahí y dejan el código en `error`); el resto de respuestas ponen la frase en `error`.
        throw new Error(data.message || data.error || t('waConnect.errorConnect'));
      }

      console.log('WhatsApp API conectado:', data);
      onSuccess && onSuccess(data);
    } catch (err) {
      console.error('Error connecting WhatsApp API:', err);
      setError(err.message || t('waConnect.errorVerify'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>{t('waConnect.title')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ marginBottom: 16, color: '#666', fontSize: 14 }}>
            {t('waConnect.description')}
          </p>

          {error && (
            <div style={{
              padding: 12,
              marginBottom: 16,
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: 4,
              color: '#c33'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Access Token <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="EAAxxxx..."
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
                disabled={loading}
              />
              <small style={{ color: '#666', fontSize: 12 }}>
                {t('waConnect.tokenHelp')}
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Phone Number ID <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.target.value)}
                placeholder="123456789012345"
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
                disabled={loading}
              />
              <small style={{ color: '#666', fontSize: 12 }}>
                {t('waConnect.phoneIdHelp')}
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                WhatsApp Business Account ID (WABA) <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={wabaId}
                onChange={(e) => setWabaId(e.target.value)}
                placeholder="987654321098765"
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
                disabled={loading}
              />
              <small style={{ color: '#666', fontSize: 12 }}>
                {t('waConnect.wabaIdHelp')}
              </small>
            </div>

            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => setShowHelp(!showHelp)}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  padding: '8px 12px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                📄 {showHelp ? t('waConnect.hideHelp') : t('waConnect.showHelp')}
              </button>

              {showHelp && (
                <div style={{
                  marginTop: 12,
                  padding: 16,
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: 4,
                  fontSize: 13,
                  lineHeight: 1.6
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>{t('waConnect.guideTitle')}</h4>

                  <div style={{ marginBottom: 12 }}>
                    <strong>1. Access Token:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                      {t('waConnect.guideToken', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <strong>2. Phone Number ID:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                      {t('waConnect.guidePhone', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>

                  <div>
                    <strong>3. WABA ID:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                      {t('waConnect.guideWaba', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions" style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: 12,
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                {tc('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: 12,
                  border: 'none',
                  background: loading ? '#ccc' : '#25D366',
                  color: 'white',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                {loading ? t('waConnect.connecting') : t('waConnect.connectBtn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
